/**
 * When an announcement shows, as the MD thinks of it ("now, until I take it
 * down", "tomorrow at 8, for a week") rather than two raw timestamps.
 *
 * Quick end choices ("end of today", "in 7 days") are kept as choices, not
 * turned into times as soon as they are picked, so a form left open over
 * lunch still means "end of today" when it is finally published.
 */

export type EndChoice = 'never' | 'today' | 'tomorrow' | 'week' | 'date';

export interface WhenValue {
  start: 'now' | 'later';
  /** Local date `YYYY-MM-DD`, used when `start` is 'later'. */
  startDate: string;
  /** Local time `HH:mm`, used when `start` is 'later'. */
  startTime: string;
  end: EndChoice;
  /** Local date and time, used when `end` is 'date'. */
  endDate: string;
  endTime: string;
}

export const END_CHOICES: { value: EndChoice; label: string }[] = [
  { value: 'never', label: 'When I take it down' },
  { value: 'today', label: 'End of today' },
  { value: 'tomorrow', label: 'End of tomorrow' },
  { value: 'week', label: 'In 7 days' },
  { value: 'date', label: 'On a date…' },
];

const pad = (n: number) => String(n).padStart(2, '0');

export function localDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function localTime(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function atLocal(date: string, time: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm] = time.split(':').map(Number) as [number, number];
  return new Date(y, m - 1, d, hh, mm, 0, 0);
}

function endOfDay(d: Date, plusDays = 0): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + plusDays, 23, 59, 0, 0);
}

/** A new announcement: live now, until it is taken down. */
export function defaultWhen(now = new Date()): WhenValue {
  const tomorrow8 = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 8, 0);
  return {
    start: 'now',
    startDate: localDate(tomorrow8),
    startTime: '08:00',
    end: 'never',
    endDate: localDate(endOfDay(now, 7)),
    endTime: '23:59',
  };
}

/** The form's value for a saved announcement's window, exact to the minute. */
export function whenFromSaved(
  startsAt: string | null,
  endsAt: string | null,
  now = new Date(),
): WhenValue {
  const base = defaultWhen(now);
  const start = startsAt ? new Date(startsAt) : null;
  const end = endsAt ? new Date(endsAt) : null;
  return {
    ...base,
    // A start already passed reads as "now": it is live, and saving it again
    // must not be refused as "a start in the past".
    ...(start && start > now
      ? { start: 'later' as const, startDate: localDate(start), startTime: localTime(start) }
      : {}),
    ...(end ? { end: 'date' as const, endDate: localDate(end), endTime: localTime(end) } : {}),
  };
}

export interface ResolvedWhen {
  /** ISO, or null for "as soon as it is published". */
  startsAt: string | null;
  /** ISO, or null for "until taken down". */
  endsAt: string | null;
  /** Plain words for what is wrong, or null when it can be saved. */
  problem: string | null;
}

/** Turns the choices into the two times the server stores, at `now`. */
export function resolveWhen(when: WhenValue, now = new Date()): ResolvedWhen {
  let start: Date | null = null;
  if (when.start === 'later') {
    start = atLocal(when.startDate, when.startTime);
    if (!start) return { startsAt: null, endsAt: null, problem: 'Choose the day and time it should start.' };
    if (start <= now) {
      return { startsAt: null, endsAt: null, problem: 'That start time has already passed. Choose a later time, or Now.' };
    }
  }

  const from = start ?? now;
  let end: Date | null = null;
  switch (when.end) {
    case 'never':
      break;
    case 'today':
      end = endOfDay(from);
      break;
    case 'tomorrow':
      end = endOfDay(from, 1);
      break;
    case 'week':
      end = endOfDay(from, 7);
      break;
    case 'date':
      end = atLocal(when.endDate, when.endTime);
      if (!end) return { startsAt: null, endsAt: null, problem: 'Choose the day it should come down.' };
      break;
  }

  if (end && end <= from) {
    return {
      startsAt: null,
      endsAt: null,
      problem: start
        ? 'It would come down before it starts. Choose a later end.'
        : 'That end time has already passed. Choose a later one.',
    };
  }

  return {
    startsAt: start ? start.toISOString() : null,
    endsAt: end ? end.toISOString() : null,
    problem: null,
  };
}

const dayFormat = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const timeFormat = new Intl.DateTimeFormat('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });

function when(d: Date, now: Date): string {
  const today = localDate(now);
  const tomorrow = localDate(endOfDay(now, 1));
  const day =
    localDate(d) === today ? 'today' : localDate(d) === tomorrow ? 'tomorrow' : dayFormat.format(d);
  return `${day} at ${timeFormat.format(d).replace(' ', ' ')}`;
}

/** One line for the summary row: "Now, until you take it down". */
export function describeWhen(value: WhenValue, now = new Date()): string {
  const resolved = resolveWhen(value, now);
  const starts = value.start === 'now' ? 'Now' : resolved.startsAt ? `From ${when(new Date(resolved.startsAt), now)}` : 'Later';
  if (value.end === 'never') return `${starts}, until you take it down`;
  if (!resolved.endsAt) return starts;
  return `${starts}, until ${when(new Date(resolved.endsAt), now)}`;
}
